import { toast } from 'sonner';
import type { ReactNode } from 'react';
import { Toaster } from './ui/sonner.js';

/**
 * Feedback de ação (sonner por baixo).
 *
 * A API é a mesma de antes — `success`, `failure`, `push` — para nenhuma tela precisar
 * mudar. O que muda é o comportamento: erro de ação vira um toast que some sozinho, e o
 * request id continua indo junto quando existe (§33), porque é ele que transforma "deu
 * erro" em algo rastreável no Log Explorer.
 */
export interface Toast {
  title: string;
  body?: string;
  tone?: 'ok' | 'danger' | 'info' | 'warn';
}

interface ToastApi {
  push: (toast: Toast) => void;
  success: (title: string, body?: string) => void;
  /** Surfaces the request id when the failure carries one (PRD §33). */
  failure: (title: string, error: unknown) => void;
}

const api: ToastApi = {
  push: ({ title, body, tone }) => {
    const options = body ? { description: body } : undefined;
    if (tone === 'ok') toast.success(title, options);
    else if (tone === 'danger') toast.error(title, options);
    else if (tone === 'warn') toast.warning(title, options);
    else toast(title, options);
  },
  success: (title, body) => api.push({ title, ...(body ? { body } : {}), tone: 'ok' }),
  failure: (title, error) => {
    const apiError = error as { message?: string; requestId?: string | null };
    const body = [apiError?.message, apiError?.requestId && `Request ID: ${apiError.requestId}`]
      .filter(Boolean)
      .join(' · ');
    api.push({ title, ...(body ? { body } : {}), tone: 'danger' });
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster />
    </>
  );
}

export function useToast(): ToastApi {
  return api;
}
